import { useState, useCallback } from 'react';
import APIs from '../../../../../application/APIs';
import { Placeholder } from './types';

interface AutoFillState {
  selectedClientId: string | null;
  selectedBankAccountId: string | null;
}

interface UseAutoFillProps {
  getPlaceholders: () => Placeholder[];
  updatePlaceholdersValues: (updater: (placeholders: Placeholder[]) => Placeholder[]) => void;
}

export const useAutoFill = ({ getPlaceholders, updatePlaceholdersValues }: UseAutoFillProps) => {
  const [state, setState] = useState<AutoFillState>({
    selectedClientId: null,
    selectedBankAccountId: null,
  });

  const setSelectedClientId = useCallback((clientId: string | null) => {
    setState(s => ({ ...s, selectedClientId: clientId }));
  }, []);

  const setSelectedBankAccountId = useCallback((bankAccountId: string | null) => {
    setState(s => ({ ...s, selectedBankAccountId: bankAccountId }));
  }, []);

  const autoFillPlaceholders = useCallback(async (clientId: string, bankAccountId?: string) => {
    try {
      const response = await APIs.getClientRelatedData(clientId);
      const relatedData = response.related_data;

      setState(s => ({
        ...s,
        selectedClientId: clientId,
        selectedBankAccountId: bankAccountId || null,
      }));

      updatePlaceholdersValues(placeholders =>
        placeholders.map(p => {
          const [collection, field] = p.db_column.split('.');

          if (collection === 'CLIENTS' && relatedData.client) {
            const value = relatedData.client[field];
            return { ...p, currentValue: value ? String(value) : '' };
          }

          if (collection === 'COMPANIES' && relatedData.companies?.length > 0) {
            const value = relatedData.companies[0][field];
            return { ...p, currentValue: value ? String(value) : '' };
          }

          if (collection === 'BANK_ACCOUNTS') {
            // If a specific bank account is selected, use that one
            // Otherwise use the first one
            const bankAccounts = relatedData.bank_accounts || [];
            let bank = bankAccounts[0];
            if (bankAccountId) {
              const found = bankAccounts.find((b: Record<string, unknown>) => b.id === bankAccountId);
              if (found) bank = found;
            }
            if (bank) {
              const value = bank[field];
              return { ...p, currentValue: value ? String(value) : '' };
            }
          }

          return p;
        })
      );
    } catch (error) {
      console.error('Failed to auto-fill placeholders:', error);
    }
  }, [updatePlaceholdersValues]);

  // Get field values map for generating filled PDF
  const getFieldValues = useCallback(() => {
    const values: Record<string, string> = {};
    getPlaceholders().forEach(p => {
      if (p.currentValue) {
        values[p.db_column] = p.currentValue;
      }
    });
    return values;
  }, [getPlaceholders]);

  // Get required collections based on placeholders
  const getRequiredCollections = useCallback(() => {
    const collections = new Set<string>();
    getPlaceholders().forEach(p => {
      const [collection] = p.db_column.split('.');
      collections.add(collection);
    });
    return Array.from(collections);
  }, [getPlaceholders]);

  return {
    selectedClientId: state.selectedClientId,
    selectedBankAccountId: state.selectedBankAccountId,
    setSelectedClientId,
    setSelectedBankAccountId,
    autoFillPlaceholders,
    getFieldValues,
    getRequiredCollections,
  };
};
